var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { IMALoader } from "./ima-loader";
import { AdEventType } from "../types";
import { createImaOverlay, initImaAdsManagerToViewport } from "./ima-overlay";
/**
 * IMA Interstitial Video Ads Implementation
 * Manages interstitial video ad playback using Google IMA SDK
 *
 * Note: No reward logic - just play and close
 */
export class IMAInterstitialVideo {
    constructor(eventEmitter) {
        this.eventEmitter = eventEmitter;
        this.adDisplayContainer = null;
        this.adsLoader = null;
        this.adsManager = null;
        this.videoElement = null;
        this.containerElement = null;
        this.adContainerElement = null;
        /** Tear-down for the adsManager viewport-resize listeners. See ima-overlay.ts. */
        this.detachViewportResize = null;
        this.resolvePromise = null;
        this.rejectPromise = null;
    }
    /**
     * Show interstitial video ad
     * CRITICAL: This must be called from a user gesture (click, touch)
     */
    show(config, adTagUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            // Ensure IMA SDK is loaded
            yield IMALoader.load();
            // Emit ad requested event
            yield this.eventEmitter.emit(AdEventType.AD_REQUESTED, {
                placement: config.placement,
                adType: "interstitial",
            });
            return new Promise((resolve, reject) => {
                this.resolvePromise = resolve;
                this.rejectPromise = reject;
                try {
                    // Create UI elements
                    this.createVideoContainer();
                    // Initialize IMA
                    this.initializeIMA();
                    // CRITICAL: Initialize AdDisplayContainer synchronously in user gesture
                    // This MUST happen before requestAds() or IMA will mark request as invalid
                    this.adDisplayContainer.initialize();
                    console.log("[GetJarAds] AdDisplayContainer initialized (user gesture)");
                    // Now request ads
                    this.requestAds(adTagUrl);
                    console.log(`[GetJarAds] Interstitial video requested for ${config.placement}`);
                }
                catch (error) {
                    this.cleanup();
                    reject(error);
                }
            });
        });
    }
    /**
     * Create the fullscreen ad container using the shared IMA overlay
     * builder (see `ima-overlay.ts`). Same DOM shape + stacking + CSS
     * rule as the rewarded renderer; behavioural differences (close X,
     * reward semantics) live in the renderers, not the overlay.
     */
    createVideoContainer() {
        const overlay = createImaOverlay('interstitial');
        this.containerElement = overlay.containerElement;
        this.adContainerElement = overlay.adContainerElement;
        this.videoElement = overlay.videoElement;
        document.body.appendChild(this.containerElement);
    }
    /**
     * Initialize IMA SDK components
     */
    initializeIMA() {
        const ima = IMALoader.getSDK();
        // AdDisplayContainer target is the overlay div, NOT the fullscreen
        // backdrop — see createVideoContainer() for the layout rationale.
        this.adDisplayContainer = new ima.AdDisplayContainer(this.adContainerElement, this.videoElement);
        // Create AdsLoader
        this.adsLoader = new ima.AdsLoader(this.adDisplayContainer);
        // Listen for AdsManager loaded
        this.adsLoader.addEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.onAdsManagerLoaded.bind(this), false);
        // Listen for ad errors
        this.adsLoader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, this.onAdError.bind(this), false);
    }
    /**
     * Request ads from VAST URL
     */
    requestAds(adTagUrl) {
        const ima = IMALoader.getSDK();
        const adsRequest = new ima.AdsRequest();
        // CRITICAL: Ensure output=vast (IMA may try to use xml_vast4)
        const normalizedUrl = adTagUrl.replace("output=xml_vast4", "output=vast");
        adsRequest.adTagUrl = normalizedUrl;
        adsRequest.linearAdSlotWidth = window.innerWidth;
        adsRequest.linearAdSlotHeight = window.innerHeight;
        adsRequest.nonLinearAdSlotWidth = window.innerWidth;
        adsRequest.nonLinearAdSlotHeight = window.innerHeight;
        // CRITICAL: Tell IMA this is user-initiated autoplay (required for Chrome)
        adsRequest.setAdWillAutoPlay(true);
        adsRequest.setAdWillPlayMuted(false);
        this.adsLoader.requestAds(adsRequest);
    }
    /**
     * Handle AdsManager loaded event
     */
    onAdsManagerLoaded(adsManagerLoadedEvent) {
        const ima = IMALoader.getSDK();
        // Get AdsManager
        const adsRenderingSettings = new ima.AdsRenderingSettings();
        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
        this.adsManager = adsManagerLoadedEvent.getAdsManager(this.videoElement, adsRenderingSettings);
        // Add event listeners
        this.adsManager.addEventListener(ima.AdEvent.Type.LOADED, this.onAdLoaded.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.STARTED, this.onAdStarted.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.COMPLETE, this.onAdComplete.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.SKIPPED, this.onAdSkipped.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.USER_CLOSE, this.onAdUserClose.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, this.onAllAdsCompleted.bind(this));
        this.adsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, this.onAdError.bind(this));
        try {
            // AdDisplayContainer already initialized in show() (user gesture).
            // Wire the adsManager to the viewport — init + follow-up resize +
            // window resize/orientationchange listeners — via the shared
            // helper so this stays in sync with the rewarded renderer.
            this.detachViewportResize = initImaAdsManagerToViewport(this.adsManager, ima, this.adContainerElement, 'interstitial');
            // Start ad playback
            this.adsManager.start();
        }
        catch (error) {
            console.error("[GetJarAds] Failed to start AdsManager:", error);
            this.onAdError(error);
        }
    }
    /**
     * Handle ad loaded event
     */
    onAdLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] Interstitial video loaded");
            yield this.eventEmitter.emit(AdEventType.AD_LOADED, {
                adType: "interstitial",
            });
        });
    }
    /**
     * Handle ad started event
     */
    onAdStarted() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] Interstitial video started");
            yield this.eventEmitter.emit(AdEventType.AD_STARTED, {
                adType: "interstitial",
            });
            yield this.eventEmitter.emit(AdEventType.AD_IMPRESSION, {
                adType: "interstitial",
            });
        });
    }
    /**
     * Handle ad complete event
     */
    onAdComplete() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] Interstitial video completed");
            yield this.eventEmitter.emit(AdEventType.AD_COMPLETED, {
                adType: "interstitial",
            });
        });
    }
    /**
     * Handle ad skipped event
     */
    onAdSkipped() {
        console.log("[GetJarAds] Interstitial video skipped");
        this.finish();
    }
    /**
     * Handle user close event
     */
    onAdUserClose() {
        console.log("[GetJarAds] Interstitial video closed by user");
        this.finish();
    }
    /**
     * Handle all ads completed event
     */
    onAllAdsCompleted() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] All ads completed");
            yield this.eventEmitter.emit(AdEventType.AD_CLOSED, {
                adType: "interstitial",
            });
            this.finish();
        });
    }
    /**
     * Handle ad error event
     */
    onAdError(adErrorEvent) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const errorMessage = ((_c = (_b = (_a = adErrorEvent === null || adErrorEvent === void 0 ? void 0 : adErrorEvent.getError) === null || _a === void 0 ? void 0 : _a.call(adErrorEvent)) === null || _b === void 0 ? void 0 : _b.getMessage) === null || _c === void 0 ? void 0 : _c.call(_b)) || "Unknown error";
            console.error("[GetJarAds] Interstitial video error:", errorMessage);
            yield this.eventEmitter.emit(AdEventType.AD_ERROR, {
                adType: "interstitial",
                error: errorMessage,
            });
            this.cleanup();
            if (this.rejectPromise) {
                this.rejectPromise(new Error(errorMessage));
                this.rejectPromise = null;
            }
        });
    }
    /**
     * Finish ad playback
     */
    finish() {
        this.cleanup();
        if (this.resolvePromise) {
            this.resolvePromise();
            this.resolvePromise = null;
        }
    }
    /**
     * Cleanup IMA resources
     */
    cleanup() {
        // Tear down the viewport-resize listeners BEFORE destroying
        // adsManager — a late resize event firing against a destroyed
        // adsManager would throw.
        if (this.detachViewportResize) {
            this.detachViewportResize();
            this.detachViewportResize = null;
        }
        // Destroy AdsManager
        if (this.adsManager) {
            try {
                this.adsManager.destroy();
            }
            catch (e) {
                console.warn("[GetJarAds] Error destroying AdsManager:", e);
            }
            this.adsManager = null;
        }
        // Destroy AdsLoader
        if (this.adsLoader) {
            try {
                this.adsLoader.destroy();
            }
            catch (e) {
                console.warn("[GetJarAds] Error destroying AdsLoader:", e);
            }
            this.adsLoader = null;
        }
        // Remove DOM elements
        if (this.containerElement && this.containerElement.parentNode) {
            this.containerElement.parentNode.removeChild(this.containerElement);
        }
        this.adDisplayContainer = null;
        this.videoElement = null;
        this.adContainerElement = null;
        this.containerElement = null;
    }
    /**
     * Destroy player
     */
    destroy() {
        this.cleanup();
        this.resolvePromise = null;
        this.rejectPromise = null;
    }
}
